import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { toast, toastError } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import type { SpeedBackend } from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';
import { markLocalLimitCancel } from '../utils/localLimitCancels';

import { waitUntilGone } from './useCloseActiveTrade';

type OrderState = 'busy' | 'error';

/**
 * Cancel / "fill now" for an OFF-CHART resting limit order, addressed by coin.
 *
 * The charted market goes through `useRealTradeActions` instead, which drives
 * the execution hook's slot (spinner, optimistic status, the whole lifecycle) —
 * the same rule the close buttons follow. Acting on the charted market from
 * here would leave that slot stuck on `resting` over an order that no longer
 * exists, and `_syncExecution` would write it straight back into the store.
 *
 * Off-chart rows have no slot to drive, so these go straight to the endpoints
 * and wait for the row to leave the active list before they stop spinning.
 */
export function useLimitOrderActions() {
    const [orderStates, setOrderStates] = useState<Map<string, OrderState>>(
        () => new Map(),
    );
    const backendRef = useRef<SpeedBackend | null>(null);
    backendRef.current = useSpeedBackend();

    const queryClient = useQueryClient();
    const networkId = useTokenPairStore((s) => s.networkId);

    const run = useCallback(
        async (
            action: 'cancel' | 'fill',
            key: string,
            coin: string,
            tradeHash?: string,
        ) => {
            const store = useTradingStore.getState();
            if (store.runtimeMode !== 'real') {
                if (action === 'cancel') {
                    store.cancelDemoRestingOrder(coin);
                    toast.success('Limit order cancelled', { duration: 6000 });
                } else {
                    store.fillDemoRestingOrder(coin);
                    toast.success('Limit order filled, position is running', {
                        duration: 6000,
                    });
                }
                return;
            }

            const api = backendRef.current;
            if (!api) {
                toast.error(
                    'Your session is still connecting. Try again in a moment.',
                );
                return;
            }

            setOrderStates((prev) => new Map(prev).set(key, 'busy'));
            try {
                // "Fill now" cancels the resting order server-side before it
                // opens at market, and that cancel is published like any other.
                // Claim it now, or the user gets told their own Fill now was a
                // cancellation (see localLimitCancels).
                if (action === 'fill') markLocalLimitCancel(tradeHash ?? null);
                const result =
                    action === 'cancel'
                        ? await api.cancelLimitOrder({ coin })
                        : await api.executeLimitOrder({ coin });

                if (result.status === 'Cancelled') {
                    // this tab announced it, so the websocket push must not
                    // announce it again (see localLimitCancels).
                    markLocalLimitCancel(result.tradeHash);
                    toast.success('Limit order cancelled', { duration: 6000 });
                } else {
                    toast.success('Limit order filled, position is running', {
                        duration: 8000,
                    });
                }

                // Stop spinning only once the row is actually gone from the
                // active list. Clearing on the HTTP response alone puts the old
                // resting row back under an idle button for a poll or two,
                // which invites a second click on an order that no longer
                // exists. Same wait the off-chart close does.
                const tradesApi = backendRef.current;
                if (tradesApi && tradeHash) {
                    await waitUntilGone(
                        tradesApi,
                        networkId,
                        tradeHash,
                        queryClient,
                    );
                }

                setOrderStates((prev) => {
                    const next = new Map(prev);
                    next.delete(key);
                    return next;
                });
            } catch (error) {
                toastError(error, {
                    operation:
                        action === 'cancel' ? 'Cancel order' : 'Fill order',
                });
                setOrderStates((prev) => new Map(prev).set(key, 'error'));
            } finally {
                // whatever happened, the lists are the truth about what the
                // user holds now — an order that filled underneath a cancel
                // shows up as the position it became.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.active(networkId),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.history(networkId),
                });
                // a partially filled limit leaves a remainder resting that no
                // row covers — that is the whole reason the panel exists.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.exposure(networkId),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.balance(),
                });
            }
        },
        [networkId, queryClient],
    );

    const cancelOrder = useCallback(
        (key: string, coin: string, tradeHash?: string) =>
            run('cancel', key, coin, tradeHash),
        [run],
    );
    const fillOrder = useCallback(
        (key: string, coin: string, tradeHash?: string) =>
            run('fill', key, coin, tradeHash),
        [run],
    );

    return { cancelOrder, fillOrder, orderStates };
}
